import type { OpenPosition } from "./types";

/**
 * One symbol across every account, with the per-book holdings kept underneath.
 *
 * The merged figures answer "how much of this do I hold"; the legs answer
 * "where is it". Neither replaces the other, so both are carried.
 */
export interface PositionGroup {
  symbol: string;
  legs: OpenPosition[];
  books: number;
  qty: number;
  cost_basis: number;
  /** The same for every leg — it is one stock — so it is taken, not summed. */
  last_price: number | null;
  day_change_pct: number | null;
  day_pnl: number | null;
  /** Cost over quantity — a blended entry across accounts, not an average of averages. */
  avg_entry: number;
  /** Null when any leg could not be priced: a partial total is not a total. */
  position_value: number | null;
  sizing_pct: number;
  days_held: number;
  /** Only when every leg agrees; a blended stop is not a stop. */
  sl_pct: number | null;
  risk_pct: number | null;
  open_risk_pct: number | null;
  unrealized_pnl: number | null;
  unrealized_pct: number | null;
  /** The worst and best any leg reached — the group's true extremes. */
  mae_pct: number | null;
  mfe_pct: number | null;
  given_back_pct: number | null;
  portfolio_gain_pct: number | null;
  r_multiple: number | null;
  /** True when any leg is margin funded — exposure is the thing being flagged. */
  mtf: boolean;
  /** Any leg averaging down makes the merged row say so. */
  averaging_down: boolean;
  lots: OpenPosition["lots"];
  /** The same for every leg — one stock has one classification. */
  sector: string | null;
  industry: string | null;
  basic_industry: string | null;
  stale_price: boolean;
  anomaly: string | null;
}

const sum = (values: number[]) => values.reduce((a, v) => a + v, 0);

/** Totals a column, or null if any row is missing it. */
function total(legs: OpenPosition[], pick: (p: OpenPosition) => number | null): number | null {
  const values = legs.map(pick);
  return values.some((v) => v === null) ? null : sum(values as number[]);
}

/** The furthest any leg went, or null when none of them knows. */
function extreme(
  legs: OpenPosition[],
  pick: (p: OpenPosition) => number | null,
  choose: (...values: number[]) => number,
): number | null {
  const known = legs.map(pick).filter((v): v is number => v !== null);
  return known.length === 0 ? null : choose(...known);
}

export function groupBySymbol(rows: OpenPosition[]): PositionGroup[] {
  const bySymbol = new Map<string, OpenPosition[]>();
  for (const row of rows) {
    const legs = bySymbol.get(row.symbol);
    if (legs) legs.push(row);
    else bySymbol.set(row.symbol, [row]);
  }

  return [...bySymbol.values()].map((legs) => {
    const cost = sum(legs.map((l) => l.cost_basis));
    const pnl = total(legs, (l) => l.unrealized_pnl);
    const risk = total(legs, (l) => l.risk_pct);
    const riskRupees = total(legs, (l) => l.risk_rupees);
    const stops = new Set(legs.map((l) => l.sl_pct));

    return {
      symbol: legs[0].symbol,
      legs,
      books: new Set(legs.map((l) => l.tradebook_id)).size,
      qty: sum(legs.map((l) => l.qty)),
      cost_basis: cost,
      last_price: legs[0].last_price,
      day_change_pct: legs[0].day_change_pct,
      day_pnl: total(legs, (l) => l.day_pnl),
      avg_entry: cost / (sum(legs.map((l) => l.qty)) || 1),
      position_value: total(legs, (l) => l.position_value),
      // Both are shares of the same combined capital, so they add up.
      sizing_pct: sum(legs.map((l) => l.sizing_pct)),
      // How long any of it has been held — the exposure started with the first.
      days_held: Math.max(...legs.map((l) => l.days_held)),
      sl_pct: stops.size === 1 ? legs[0].sl_pct : null,
      risk_pct: risk,
      open_risk_pct: total(legs, (l) => l.open_risk_pct),
      unrealized_pnl: pnl,
      // Recomputed against the combined cost, not averaged: averaging two
      // percentages would weight a tiny holding the same as a large one.
      unrealized_pct: pnl === null || cost === 0 ? null : (pnl / cost) * 100,
      // The extreme of the group is the extreme of its legs, not their sum —
      // two accounts holding the same stock saw the same low.
      mae_pct: extreme(legs, (l) => l.mae_pct, Math.min),
      mfe_pct: extreme(legs, (l) => l.mfe_pct, Math.max),
      given_back_pct: extreme(legs, (l) => l.given_back_pct, Math.max),
      portfolio_gain_pct: total(legs, (l) => l.portfolio_gain_pct),
      r_multiple: pnl === null || !riskRupees ? null : pnl / riskRupees,
      mtf: legs.some((l) => l.product === "mtf"),
      averaging_down: legs.some((l) => l.averaging_down),
      // The merged row's own disclosure shows the books; parcels stay with
      // the leg they belong to.
      lots: [],
      sector: legs[0].sector,
      industry: legs[0].industry,
      basic_industry: legs[0].basic_industry,
      stale_price: legs.some((l) => l.stale_price),
      anomaly: legs.find((l) => l.anomaly)?.anomaly ?? null,
    };
  });
}


/** One classification bucket — a sector, an industry — and what is in it. */
export interface Bucket<T> {
  key: string;
  rows: T[];
  value: number;
  cost: number;
  unrealized: number | null;
  share: number;
}

/**
 * Buckets rows by a classification, largest holding first.
 *
 * The subtotal is what the grouping is for: "28% of the book is in Capital
 * Goods" is the thing a sector view exists to say, and reading it off a column
 * of individual rows is exactly what a person is bad at.
 */
export function bucketBy<T extends { cost_basis: number; position_value: number | null; unrealized_pnl: number | null }>(
  rows: T[],
  of: (row: T) => string,
): Bucket<T>[] {
  const buckets = new Map<string, T[]>();
  for (const row of rows) {
    const key = of(row);
    const existing = buckets.get(key);
    if (existing) existing.push(row);
    else buckets.set(key, [row]);
  }

  const total = rows.reduce((a, r) => a + (r.position_value ?? r.cost_basis), 0) || 1;

  return [...buckets.entries()]
    .map(([key, bucketRows]) => {
      const value = bucketRows.reduce((a, r) => a + (r.position_value ?? r.cost_basis), 0);
      const pnls = bucketRows.map((r) => r.unrealized_pnl);
      return {
        key,
        rows: bucketRows,
        value,
        cost: bucketRows.reduce((a, r) => a + r.cost_basis, 0),
        // One unpriced holding makes the bucket's P&L unknown, the same rule
        // the merged rows follow: a partial total is not a total.
        unrealized: pnls.some((p) => p === null)
          ? null
          : pnls.reduce((a: number, p) => a + (p as number), 0),
        share: (value / total) * 100,
      };
    })
    .sort((a, b) => b.value - a.value);
}
